import * as THREE from 'three';
import * as Kalidokit from 'kalidokit';
import type { Landmark, NormalizedLandmark } from '@mediapipe/tasks-vision';
import { VRMHumanBoneName } from '@pixiv/three-vrm';
import type { FusedPose } from './poseFusion';
import type { HandSample } from './poseEstimation';

const LANDMARK_COUNT = 33;

// MediaPipe Pose ランドマークの左右対インデックス表。
// 0（鼻）だけ対がなく不動。1〜6 は目まわり（1 left_eye_inner, 2 left_eye, 3 left_eye_outer,
// 4 right_eye_inner, 5 right_eye, 6 right_eye_outer）で (1,4)(2,5)(3,6) が対になる、
// 単純な「奇数=left / 偶数=right の隣接ペア」ではないため、明示的なペア表で構築する。
// 7 以降（耳・口・肩・肘・手首・指・腰・膝・足首・かかと・つま先）は隣接ペア (7,8),(9,10),...,(31,32) で正しい。
const MIRROR_PAIRS: [number, number][] = [
  [1, 4], [2, 5], [3, 6],
  [7, 8], [9, 10], [11, 12], [13, 14], [15, 16], [17, 18], [19, 20], [21, 22],
  [23, 24], [25, 26], [27, 28], [29, 30], [31, 32],
];
const MIRROR_INDEX_MAP: number[] = Array.from({ length: LANDMARK_COUNT }, (_, i) => i);
for (const [a, b] of MIRROR_PAIRS) {
  MIRROR_INDEX_MAP[a] = b;
  MIRROR_INDEX_MAP[b] = a;
}

const NOSE = 0;
const LEFT_EYE = 2;
const RIGHT_EYE = 5;
const LEFT_EAR = 7;
const RIGHT_EAR = 8;
const LEFT_SHOULDER = 11;
const RIGHT_SHOULDER = 12;
const LEFT_HIP = 23;
const RIGHT_HIP = 24;

const HEAD_MAX_ANGLE = THREE.MathUtils.degToRad(60);
const HEAD_MIN_CONFIDENCE = 0.3;
// 中立姿勢（正面を向いた頭）でも、耳中心から見た鼻先は解剖学的に下向き（耳珠→鼻先は
// おおむね 10〜20° 下）になるため、ピッチ推定からこのバイアス分を差し引く。
const BASELINE_NOSE_PITCH = THREE.MathUtils.degToRad(15);
const PITCH_MAX_ANGLE = THREE.MathUtils.degToRad(45);

/** world landmarks（メートル座標）を左右反転する。x を反転し、左右対のランドマークを入れ替える。 */
function mirrorWorld(landmarks: Landmark[]): Landmark[] {
  return landmarks.map((_, i) => {
    const src = landmarks[MIRROR_INDEX_MAP[i]];
    return { x: -src.x, y: src.y, z: src.z, visibility: src.visibility };
  });
}

/** 画像正規化座標（0〜1）を左右反転する。x は 1-x、左右対のランドマークを入れ替える。 */
function mirror2d(landmarks: NormalizedLandmark[]): NormalizedLandmark[] {
  return landmarks.map((_, i) => {
    const src = landmarks[MIRROR_INDEX_MAP[i]];
    return { x: 1 - src.x, y: src.y, z: src.z, visibility: src.visibility };
  });
}

/**
 * 片手のランドマークを左右反転する。body の MIRROR_INDEX_MAP（33点中の左右対を入れ替える）とは
 * 異なり、1つの手の21点はそもそも左右対を持たない（1つの手の中で完結した構造）ため、
 * 対応する landmark を入れ替える必要はなく、座標の反転だけでよい。左右どちらの手として
 * 扱うかは呼び出し側（solvePose）が leftHand/rightHand を入れ替えることで表現する。
 * side は呼び出し側が決める「反転後にどちらの手として扱うか」をそのまま持たせる。
 * worldLandmarks は mirrorWorld と同じ x 反転、landmarks（画像正規化座標）は mirror2d と
 * 同じ 1-x 反転にする。
 */
function mirrorHandSample(hand: HandSample, side: 'Left' | 'Right'): HandSample {
  return {
    side,
    score: hand.score,
    worldLandmarks: hand.worldLandmarks.map((lm) => ({ x: -lm.x, y: lm.y, z: lm.z, visibility: lm.visibility })),
    landmarks: hand.landmarks.map((lm) => ({ x: 1 - lm.x, y: lm.y, z: lm.z, visibility: lm.visibility })),
  };
}

/** Kalidokit.Hand.solve が返すオブジェクトの1関節分の値（THand のバリュー型）。 */
interface HandJointRotation {
  x: number;
  y: number;
  z: number;
}

/**
 * Kalidokit の指キー（THand のキー、例: 'LeftThumbProximal'）→ VRM 指ボーン名の対応表。
 * Wrist は含めない（LeftHand/RightHand は Kalidokit.Pose.solve の結果を既に採用しているため）。
 *
 * 注意: 親指だけ Kalidokit と VRM で関節名の基準が1つずれる。Kalidokit は他の指と同様
 * Proximal/Intermediate/Distal の3回転を返すが、VRM の親指ボーンは
 * Metacarpal → Proximal → Distal という3ボーン構成（Intermediate が無い）。
 * どちらも「手首寄りから指先へ3つの回転」という点は同じなので、名前を1つずつずらして
 * 対応させる（Kalidokit の ThumbProximal は手首直後の関節＝VRM の ThumbMetacarpal に相当）。
 */
const FINGER_JOINT_MAP: { kalidokitSuffix: string; leftBone: VRMHumanBoneName; rightBone: VRMHumanBoneName }[] = [
  { kalidokitSuffix: 'ThumbProximal', leftBone: VRMHumanBoneName.LeftThumbMetacarpal, rightBone: VRMHumanBoneName.RightThumbMetacarpal },
  { kalidokitSuffix: 'ThumbIntermediate', leftBone: VRMHumanBoneName.LeftThumbProximal, rightBone: VRMHumanBoneName.RightThumbProximal },
  { kalidokitSuffix: 'ThumbDistal', leftBone: VRMHumanBoneName.LeftThumbDistal, rightBone: VRMHumanBoneName.RightThumbDistal },
  { kalidokitSuffix: 'IndexProximal', leftBone: VRMHumanBoneName.LeftIndexProximal, rightBone: VRMHumanBoneName.RightIndexProximal },
  { kalidokitSuffix: 'IndexIntermediate', leftBone: VRMHumanBoneName.LeftIndexIntermediate, rightBone: VRMHumanBoneName.RightIndexIntermediate },
  { kalidokitSuffix: 'IndexDistal', leftBone: VRMHumanBoneName.LeftIndexDistal, rightBone: VRMHumanBoneName.RightIndexDistal },
  { kalidokitSuffix: 'MiddleProximal', leftBone: VRMHumanBoneName.LeftMiddleProximal, rightBone: VRMHumanBoneName.RightMiddleProximal },
  { kalidokitSuffix: 'MiddleIntermediate', leftBone: VRMHumanBoneName.LeftMiddleIntermediate, rightBone: VRMHumanBoneName.RightMiddleIntermediate },
  { kalidokitSuffix: 'MiddleDistal', leftBone: VRMHumanBoneName.LeftMiddleDistal, rightBone: VRMHumanBoneName.RightMiddleDistal },
  { kalidokitSuffix: 'RingProximal', leftBone: VRMHumanBoneName.LeftRingProximal, rightBone: VRMHumanBoneName.RightRingProximal },
  { kalidokitSuffix: 'RingIntermediate', leftBone: VRMHumanBoneName.LeftRingIntermediate, rightBone: VRMHumanBoneName.RightRingIntermediate },
  { kalidokitSuffix: 'RingDistal', leftBone: VRMHumanBoneName.LeftRingDistal, rightBone: VRMHumanBoneName.RightRingDistal },
  { kalidokitSuffix: 'LittleProximal', leftBone: VRMHumanBoneName.LeftLittleProximal, rightBone: VRMHumanBoneName.RightLittleProximal },
  { kalidokitSuffix: 'LittleIntermediate', leftBone: VRMHumanBoneName.LeftLittleIntermediate, rightBone: VRMHumanBoneName.RightLittleIntermediate },
  { kalidokitSuffix: 'LittleDistal', leftBone: VRMHumanBoneName.LeftLittleDistal, rightBone: VRMHumanBoneName.RightLittleDistal },
];

/**
 * Kalidokit.Hand.solve の結果（片手分の指関節回転）を、既存の pose Map に
 * VRM 指ボーンとして書き加える。toNormalizedQuaternion による x,z 符号反転は
 * 体幹・腕・脚と同じものをそのまま使う（Kalidokit の出力は VRM0 の生ボーン基準という点で
 * 体幹・腕・脚と共通のため）。
 */
function applyHandPose(
  pose: Map<VRMHumanBoneName, THREE.Quaternion>,
  riggedHand: Record<string, HandJointRotation>,
  side: 'Left' | 'Right',
): void {
  for (const joint of FINGER_JOINT_MAP) {
    const rotation = riggedHand[`${side}${joint.kalidokitSuffix}`];
    if (!rotation) continue;
    const bone = side === 'Left' ? joint.leftBone : joint.rightBone;
    pose.set(bone, toNormalizedQuaternion(rotation));
  }
}

/**
 * Kalidokit が出力するオイラー角（VRM0 の生ボーンにそのまま適用する想定の値）を、
 * three-vrm の正規化ヒューマノイドボーン（VRM1 相当・+Z 向き・T ポーズレスト）用に変換する。
 *
 * @pixiv/three-vrm-animation の VRMAnimationLoaderPlugin の実装を調べると、正規化ボーンは
 * レスト時に全ボーンがワールド軸へ整列した恒等回転を持つ設計になっており、ローカル回転を
 * そのまま「レストポーズからのワールド空間での差分回転」として設定してよい。
 * 一方 Kalidokit の出力値は VRM0（-Z 向き）モデルの生ボーンに直接適用する前提で計算されている。
 * VRM0 の生ボーン座標系と正規化ボーン座標系の関係は、モデル全体を Y 軸まわりに 180°
 * 回転したものとみなせる（three-vrm の VRMUtils.rotateVRM0 が行っているのもこれ）。
 * Y軸180°回転による共役変換 Q R(x,y,z) Q^-1 は R(-x, y, -z) になる
 * （X軸・Z軸はY軸180°回転で反転、Y軸自身は不変なため）ので、x と z の符号を反転して使う。
 * 実機のプレビューで腕・脚の曲がる向きが写真と一致することを確認して採用した。
 */
function toNormalizedQuaternion(e: { x: number; y: number; z: number }): THREE.Quaternion {
  const euler = new THREE.Euler(-e.x, e.y, -e.z, 'XYZ');
  return new THREE.Quaternion().setFromEuler(euler);
}

function toVector3(lm: { x: number; y: number; z: number }): THREE.Vector3 {
  return new THREE.Vector3(lm.x, lm.y, lm.z);
}

/** right・up の2軸から正規直交な右手系フレーム（回転行列）を作る。poseFusion の胴体フレームと同じ考え方。 */
function buildFrame(right: THREE.Vector3, up: THREE.Vector3): THREE.Matrix4 {
  const forward = new THREE.Vector3().crossVectors(right, up).normalize();
  const upOrtho = new THREE.Vector3().crossVectors(forward, right).normalize();
  const rightOrtho = new THREE.Vector3().crossVectors(upOrtho, forward).normalize();
  return new THREE.Matrix4().makeBasis(rightOrtho, upOrtho, forward);
}

/**
 * 頭の向きを pose ランドマーク（鼻・両耳・両目）から簡易推定する。
 * Kalidokit.Pose は頭の回転を返さないため、体幹フレームに対する相対回転として求める
 * （体が傾いていても頭がまっすぐ正面を向いていればほぼ恒等になるようにするため）。
 *
 * ヨー・ロールとピッチを別々に推定する2段階方式:
 *   1. ヨー・ロールは耳軸（headRight）と体幹の上方向（bodyUp）だけから求める
 *      （forward0 = cross(headRight, bodyUp) は体幹フレームの forward = cross(right, up) と
 *      同じ式構造なので、体幹に対してピッチがニュートラルな前方向になる）。
 *   2. ピッチは「耳中心→鼻」ベクトルの体幹上方向に対する下向き角度から求める。ただし
 *      中立姿勢でも鼻先は耳より解剖学的に下を向く（BASELINE_NOSE_PITCH）ため、その分を
 *      差し引いてから forward0 を headRight 軸まわりに回転させて反映する。
 * 目中心→鼻ベクトルをそのまま前方向として使うと、鼻が目より下にあるという解剖学的な
 * バイアスにより forward が不自然に下を向いてしまう（要修正前に数値検証で確認した）ため、
 * この方式を採用している。
 * ランドマークの信頼度が低い場合や幾何的に不安定な場合は null を返す（呼び出し側で無視する）。
 */
function estimateHeadRotation(landmarks: Landmark[]): THREE.Quaternion | null {
  const nose = landmarks[NOSE];
  const leftEye = landmarks[LEFT_EYE];
  const rightEye = landmarks[RIGHT_EYE];
  const leftEar = landmarks[LEFT_EAR];
  const rightEar = landmarks[RIGHT_EAR];
  const leftShoulder = landmarks[LEFT_SHOULDER];
  const rightShoulder = landmarks[RIGHT_SHOULDER];
  const leftHip = landmarks[LEFT_HIP];
  const rightHip = landmarks[RIGHT_HIP];

  const confidence = Math.min(
    nose.visibility ?? 0,
    leftEye.visibility ?? 0,
    rightEye.visibility ?? 0,
    leftEar.visibility ?? 0,
    rightEar.visibility ?? 0,
  );
  if (confidence < HEAD_MIN_CONFIDENCE) return null;

  // 体幹フレーム（poseFusion.computeTorsoFrame と同じ式で計算し直す）。
  const shoulderRight = new THREE.Vector3().subVectors(toVector3(leftShoulder), toVector3(rightShoulder));
  const hipRight = new THREE.Vector3().subVectors(toVector3(leftHip), toVector3(rightHip));
  const bodyRight = new THREE.Vector3().addVectors(shoulderRight, hipRight);
  const shoulderCenter = new THREE.Vector3().addVectors(toVector3(leftShoulder), toVector3(rightShoulder)).multiplyScalar(0.5);
  const hipCenter = new THREE.Vector3().addVectors(toVector3(leftHip), toVector3(rightHip)).multiplyScalar(0.5);
  const bodyUp = new THREE.Vector3().subVectors(shoulderCenter, hipCenter);
  if (bodyRight.lengthSq() < 1e-8 || bodyUp.lengthSq() < 1e-8) return null;
  bodyRight.normalize();
  bodyUp.normalize();
  const bodyFrame = buildFrame(bodyRight, bodyUp);

  // ヨー・ロール：耳→耳（headRight、bodyRight と同じ「人物の左方向」規約）と体幹の
  // 上方向から、ピッチ抜きの前方向 forward0 を求める。
  const headRight = new THREE.Vector3().subVectors(toVector3(leftEar), toVector3(rightEar));
  if (headRight.lengthSq() < 1e-8) return null;
  headRight.normalize();
  const forward0 = new THREE.Vector3().crossVectors(headRight, bodyUp);
  if (forward0.lengthSq() < 1e-8) return null;
  forward0.normalize();

  // ピッチ：耳中心→鼻ベクトルの、体幹の下方向（-bodyUp）への射影角度から、
  // 解剖学的ベースライン（中立頭部でも鼻先は耳より 10〜20° 下）を差し引いて求める。
  const earCenter = new THREE.Vector3().addVectors(toVector3(leftEar), toVector3(rightEar)).multiplyScalar(0.5);
  const noseDir2 = new THREE.Vector3().subVectors(toVector3(nose), earCenter);
  if (noseDir2.lengthSq() < 1e-8) return null;
  noseDir2.normalize();
  const downDir = bodyUp.clone().negate();
  const pitchDown = Math.asin(THREE.MathUtils.clamp(noseDir2.dot(downDir), -1, 1));
  const pitchRelative = THREE.MathUtils.clamp(
    pitchDown - BASELINE_NOSE_PITCH,
    -PITCH_MAX_ANGLE,
    PITCH_MAX_ANGLE,
  );

  // forward0 を headRight 軸まわりに pitchRelative だけ回転させてピッチを反映する
  // （pitchRelative が正 = 下を向く。Rodrigues の回転式で数値確認して採用した）。
  const pitchQuat = new THREE.Quaternion().setFromAxisAngle(headRight, pitchRelative);
  const forward = forward0.clone().applyQuaternion(pitchQuat).normalize();
  const headUp = new THREE.Vector3().crossVectors(forward, headRight).normalize();
  if (headUp.lengthSq() < 1e-8) return null;
  const headFrame = buildFrame(headRight, headUp);

  // 体幹座標系での頭の向き（相対回転）。
  const bodyQuat = new THREE.Quaternion().setFromRotationMatrix(bodyFrame);
  const headQuat = new THREE.Quaternion().setFromRotationMatrix(headFrame);
  const relativeQuat = bodyQuat.clone().invert().multiply(headQuat);

  const euler = new THREE.Euler().setFromQuaternion(relativeQuat, 'XYZ');
  // 他のボーンと同じ符号変換（x, z 反転）を適用してから、破綻を避けるためクランプする。
  const clamped = new THREE.Euler(
    THREE.MathUtils.clamp(-euler.x, -HEAD_MAX_ANGLE, HEAD_MAX_ANGLE),
    THREE.MathUtils.clamp(euler.y, -HEAD_MAX_ANGLE, HEAD_MAX_ANGLE),
    THREE.MathUtils.clamp(-euler.z, -HEAD_MAX_ANGLE, HEAD_MAX_ANGLE),
    'XYZ',
  );
  return new THREE.Quaternion().setFromEuler(clamped);
}

export interface SolvePoseOptions {
  /** true なら入力ランドマークを左右反転してから解く（鏡像ポーズを作りたい場合）。 */
  mirror?: boolean;
}

/**
 * 統合済みの pose ランドマークから、正規化ヒューマノイドボーンのローカル回転を求める。
 * Kalidokit.Pose.solve で体幹・腕・脚を、estimateHeadRotation で頭を推定する。
 */
export function solvePose(fused: FusedPose, options: SolvePoseOptions = {}): Map<VRMHumanBoneName, THREE.Quaternion> {
  const worldLandmarks = options.mirror ? mirrorWorld(fused.worldLandmarks) : fused.worldLandmarks;
  const landmarks2d = options.mirror ? mirror2d(fused.refLandmarks2d) : fused.refLandmarks2d;

  const riggedPose = Kalidokit.Pose.solve(worldLandmarks, landmarks2d, {
    runtime: 'mediapipe',
    imageSize: fused.imageSize,
  });
  if (!riggedPose) {
    throw new Error('ポーズの解決に失敗しました。写真の写り方を変えて再度お試しください。');
  }

  const pose = new Map<VRMHumanBoneName, THREE.Quaternion>();
  if (riggedPose.Hips.rotation) {
    pose.set(VRMHumanBoneName.Hips, toNormalizedQuaternion(riggedPose.Hips.rotation));
  }
  pose.set(VRMHumanBoneName.Spine, toNormalizedQuaternion(riggedPose.Spine));
  pose.set(VRMHumanBoneName.LeftUpperArm, toNormalizedQuaternion(riggedPose.LeftUpperArm));
  pose.set(VRMHumanBoneName.RightUpperArm, toNormalizedQuaternion(riggedPose.RightUpperArm));
  pose.set(VRMHumanBoneName.LeftLowerArm, toNormalizedQuaternion(riggedPose.LeftLowerArm));
  pose.set(VRMHumanBoneName.RightLowerArm, toNormalizedQuaternion(riggedPose.RightLowerArm));
  pose.set(VRMHumanBoneName.LeftHand, toNormalizedQuaternion(riggedPose.LeftHand));
  pose.set(VRMHumanBoneName.RightHand, toNormalizedQuaternion(riggedPose.RightHand));
  pose.set(VRMHumanBoneName.LeftUpperLeg, toNormalizedQuaternion(riggedPose.LeftUpperLeg));
  pose.set(VRMHumanBoneName.RightUpperLeg, toNormalizedQuaternion(riggedPose.RightUpperLeg));
  pose.set(VRMHumanBoneName.LeftLowerLeg, toNormalizedQuaternion(riggedPose.LeftLowerLeg));
  pose.set(VRMHumanBoneName.RightLowerLeg, toNormalizedQuaternion(riggedPose.RightLowerLeg));

  const headQuat = estimateHeadRotation(worldLandmarks);
  if (headQuat) pose.set(VRMHumanBoneName.Head, headQuat);

  // 手指: body のような多視点融合はせず、サイドごとにスコア最良の1フレーム
  // （poseFusion.fusePoses が選択済み）だけを Kalidokit.Hand.solve に渡す。
  // 静止ポーズなので複数フレームの手の向きを融合する必要が薄く、また手の向きの融合は
  // 精度上の課題が大きいため、単純に最良1フレームを採用する方針にした。
  let leftHandSource = fused.leftHand;
  let rightHandSource = fused.rightHand;
  if (options.mirror) {
    // body と同様に手も鏡像にする: 左右のサンプルを入れ替えたうえで、各手の
    // ランドマークを反転してから解く。どちらが鏡像として正しく振る舞うかは
    // 実機のプレビューでの確認が必要（未検証）。
    leftHandSource = fused.rightHand ? mirrorHandSample(fused.rightHand, 'Left') : undefined;
    rightHandSource = fused.leftHand ? mirrorHandSample(fused.leftHand, 'Right') : undefined;
  }

  if (leftHandSource) {
    const riggedLeftHand = Kalidokit.Hand.solve(leftHandSource.landmarks, 'Left');
    if (riggedLeftHand) applyHandPose(pose, riggedLeftHand as unknown as Record<string, HandJointRotation>, 'Left');
  }
  if (rightHandSource) {
    const riggedRightHand = Kalidokit.Hand.solve(rightHandSource.landmarks, 'Right');
    if (riggedRightHand) applyHandPose(pose, riggedRightHand as unknown as Record<string, HandJointRotation>, 'Right');
  }

  return pose;
}
